import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';

// GL-M16: blocking screen shown when the signed-in patient has
// mustChangePassword === true. The rest of the portal stays unreachable until a
// new password is set. Mirrors the backend password policy (min 12 chars with
// upper/lower/number/symbol) so we fail fast before hitting the API.
function passwordPolicyError(value: string): boolean {
  return (
    value.length < 12 ||
    !/[A-Z]/.test(value) ||
    !/[a-z]/.test(value) ||
    !/[0-9]/.test(value) ||
    !/[^A-Za-z0-9]/.test(value)
  );
}

export default function ChangePasswordGate() {
  const { t } = useTranslation();
  const { changePassword, logout } = useAuth0();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t('changePassword.fillAll'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('changePassword.mismatch'));
      return;
    }
    if (newPassword === currentPassword) {
      setError(t('changePassword.sameAsCurrent'));
      return;
    }
    if (passwordPolicyError(newPassword)) {
      setError(t('changePassword.policyError'));
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      // Backend revokes refresh tokens on change; force a clean re-login.
      setSuccess(true);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : t('changePassword.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            {t('nav.brandSub')}
          </p>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">{t('changePassword.title')}</h1>
          <p className="mt-2 text-sm text-slate-500">{t('changePassword.subtitle')}</p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {t('changePassword.success')}
            </div>
            <button
              type="button"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              {t('changePassword.signOut')}
            </button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit}>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                {t('changePassword.currentPassword')}
              </span>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                {t('changePassword.newPassword')}
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                required
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                {t('changePassword.confirmPassword')}
              </span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                required
              />
            </label>

            <p className="text-xs text-slate-500">{t('changePassword.policyHint')}</p>

            {error ? (
              <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('changePassword.submitting') : t('changePassword.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
